import { Switch } from "@mui/material";
import { CloseGreyIcon } from "../../../assets/icons";
import { UpdateLocalEventDTO } from "../../../models/localEvent/localEvent.request";
import { useEffect, useState } from "react";
import { useNotification } from "../../toster-notification/NotificationContext";
import { localEventService } from "../../../services/localEventService";
import Loader from "../../shared/Loader";
import { LocalEventSettingsDTO } from "../../../models/localEvent/localEvent.response";

type ErrorState = Partial<Record<keyof UpdateLocalEventDTO, string>>;

interface EditLocalEventViewProps {
    id: string;
    localId: string;
    onClose: () => void;
}

export default function EditLocalEventView({ onClose, localId, id }: EditLocalEventViewProps) {

    const [localEvent, setLocalEvent] = useState<UpdateLocalEventDTO>({
        title: "",
        slug: "",
        description: "",
        reservationUrl: "",
        priceText: "",
        startsAtUtc: new Date(),
        endsAtUtc: new Date(),
        visibleFromUtc: new Date(),
        visibleUntilUtc: new Date(),
        isPublished: false,
        isCancelled: false,
        isFeatured: false,
        removeImage: false
    });
    const [imageUrl, setImageUrl] = useState<string>("");
    const [previewUrl, setPreviewUrl] = useState<string>("");

    const [errors, setErrors] = useState<ErrorState>({});

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();


    useEffect(() => {
        fetchLocalEventSettings();
    }, [id, localId]);

    useEffect(() => {
        if (!localEvent.imageFile) {
            setPreviewUrl("");
            return;
        }

        const objectUrl = URL.createObjectURL(
            localEvent.imageFile
        );

        setPreviewUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [localEvent.imageFile]);


    const fetchLocalEventSettings = async () => {
        setLoading(true);
        try {
            const response = await localEventService.GetSettings(localId, id);
            if (response.success && response.data) {
                const settings: LocalEventSettingsDTO = response.data;
                setLocalEvent({
                    title: settings.title,
                    slug: settings.slug,
                    description: settings.description,
                    reservationUrl: settings.reservationUrl,
                    priceText: settings.priceText,
                    startsAtUtc: new Date(settings.startsAtUtc),
                    endsAtUtc: new Date(settings.endsAtUtc),
                    visibleFromUtc: new Date(settings.visibleFromUtc),
                    visibleUntilUtc: new Date(settings.visibleUntilUtc),
                    isPublished: settings.isPublished,
                    isCancelled: settings.isCancelled,
                    isFeatured: settings.isFeatured,
                    removeImage: false
                });
                setImageUrl(settings.imageUrl);
            } else {
                notify("error", "Došlo je do greške prilikom učitavanja podataka o događaju.");
            }
        } catch (error) {
            notify("error", "Došlo je do greške prilikom učitavanja podataka o događaju.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateLocalEvent = async () => {

        const isValid = validate();
        if (!isValid) {
            notify("error", "Proverite podatke koje ste uneli.");
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();

            formData.append("title", localEvent.title);
            formData.append("slug", localEvent.slug);
            formData.append("description", localEvent.description || "");
            formData.append("reservationUrl", localEvent.reservationUrl || "");
            formData.append("priceText", localEvent.priceText || "");
            formData.append("startsAtUtc", localEvent.startsAtUtc.toISOString());
            formData.append("endsAtUtc", localEvent.endsAtUtc.toISOString());
            formData.append("visibleFromUtc", localEvent.visibleFromUtc.toISOString());
            formData.append("visibleUntilUtc", localEvent.visibleUntilUtc.toISOString());
            formData.append("isPublished", String(localEvent.isPublished));
            formData.append("isCancelled", String(localEvent.isCancelled));
            formData.append("isFeatured", String(localEvent.isFeatured));
            formData.append("removeImage", String(localEvent.removeImage));
            if (localEvent.imageFile) {
                formData.append("imageFile", localEvent.imageFile);
            }

            const response = await localEventService.Update(localId, id, formData);

            if (response.success) {
                notify("success", "Događaj je uspešno ažuriran.");
                onClose();
            } else {
                notify("error", "Došlo je do greške prilikom ažuriranja događaja");
            }
        } catch (error) {
            notify("error", "Došlo je do greške prilikom ažuriranja događaja");
        } finally {
            setLoading(false);
        }
    }

    const getError = (field: keyof UpdateLocalEventDTO) =>
        errors[field] ? <div className="error-text">{errors[field]}</div> : null;

    const updateField = <K extends keyof UpdateLocalEventDTO>(
        field: K,
        value: UpdateLocalEventDTO[K]
    ) => {
        setLocalEvent((previousState) => ({
            ...previousState,
            [field]: value
        }));

        setErrors((previousErrors) => ({
            ...previousErrors,
            [field]: undefined
        }));
    };

    const validate = (): boolean => {
        const validationErrors: ErrorState = {};

        const title = localEvent.title.trim();
        const slug = localEvent.slug.trim();
        const description = localEvent.description?.trim() ?? "";

        // Title validacija
        if (!title) {
            validationErrors.title = "Naziv događaja je obavezan.";
        } else if (title.length > 100) {
            validationErrors.title =
                "Naziv događaja ne sme imati više od 100 karaktera.";
        }

        // Slug validacija
        if (!slug) {
            validationErrors.slug = "Slug je obavezan.";
        } else if (slug.length > 50) {
            validationErrors.slug =
                "Slug ne sme imati više od 50 karaktera.";
        }

        // Description nije obavezan, ali ima maksimalnu dužinu
        if (description.length > 500) {
            validationErrors.description =
                "Opis događaja ne sme imati više od 500 karaktera.";
        }

        const startsAt = localEvent.startsAtUtc;
        const endsAt = localEvent.endsAtUtc;
        const visibleFrom = localEvent.visibleFromUtc;
        const visibleUntil = localEvent.visibleUntilUtc;

        const startDateIsValid =
            startsAt instanceof Date &&
            !Number.isNaN(startsAt.getTime());

        const endDateIsValid =
            endsAt instanceof Date &&
            !Number.isNaN(endsAt.getTime());

        const visibleFromDateIsValid =
            visibleFrom instanceof Date &&
            !Number.isNaN(visibleFrom.getTime());

        const visibleUntilDateIsValid =
            visibleUntil instanceof Date &&
            !Number.isNaN(visibleUntil.getTime());

        // Datum početka
        if (!startDateIsValid) {
            validationErrors.startsAtUtc =
                "Datum početka događaja je obavezan.";
        }

        // Datum kraja
        if (!endDateIsValid) {
            validationErrors.endsAtUtc =
                "Datum završetka događaja je obavezan.";
        }

        // Poredimo ih samo ako su oba datuma validna
        if (startDateIsValid && endDateIsValid) {
            if (startsAt.getTime() >= endsAt.getTime()) {
                validationErrors.startsAtUtc =
                    "Početak događaja mora biti pre kraja događaja.";

                validationErrors.endsAtUtc =
                    "Kraj događaja mora biti nakon početka događaja.";
            }
        }

        // Vidljiv od
        if (!visibleFromDateIsValid) {
            validationErrors.visibleFromUtc =
                "Datum početka vidljivosti je obavezan.";
        }

        // Vidljiv do
        if (!visibleUntilDateIsValid) {
            validationErrors.visibleUntilUtc =
                "Datum završetka vidljivosti je obavezan.";
        }

        if (visibleFromDateIsValid && visibleUntilDateIsValid) {
            if (visibleFrom.getTime() >= visibleUntil.getTime()) {
                validationErrors.visibleFromUtc =
                    "Početak vidljivosti mora biti pre kraja vidljivosti.";

                validationErrors.visibleUntilUtc =
                    "Kraj vidljivosti mora biti nakon početka vidljivosti.";
            }
        }

        setErrors(validationErrors);

        return Object.keys(validationErrors).length === 0;
    };

    const uploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        setLocalEvent((previousEvent) => ({
            ...previousEvent,
            imageFile: file,
            removeImage: false
        }));

        setImageUrl("");

        setErrors((previousErrors) => ({
            ...previousErrors,
            imageFile: undefined
        }));

    }

    const formatDateForInput = (date: Date | undefined) => {
        if (!date || Number.isNaN(date.getTime())) {
            return "";
        }

        const timezoneOffset = date.getTimezoneOffset() * 60_000;
        const localDate = new Date(date.getTime() - timezoneOffset);

        return localDate.toISOString().slice(0, 16);
    };


    if (loading) {
        return <Loader />;
    }

    return (
        <section className="modal-section local-event">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div product">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Ažuriraj događaj</div>
                                <div className="description-text">Podaci se prikazuju gostima samo u periodu vidljivosti koji odredite.</div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>
                        <div className="modal-form-block w-form">
                            <form className="modal-form" >

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Naziv događaja *</label>
                                    <input className="form-input grey w-input" placeholder="npr. Veče italijanske kuhinje" type="text" maxLength={100} name="localEventTitle"
                                        value={localEvent.title}
                                        onChange={(e) => updateField("title", e.target.value)}
                                    />
                                    {getError("title")}
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Slug *</label>
                                    <input className="form-input grey w-input" placeholder="npr. vece-italijanske-kuhinje" type="text" maxLength={50} name="localEventSlug"
                                        value={localEvent.slug}
                                        onChange={(e) => updateField("slug", e.target.value)}
                                    />
                                    {getError("slug")}
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Opis</label>
                                    <textarea placeholder="Kratak opis događaja" className="form-textarea grey w-input" maxLength={500}
                                        value={localEvent.description}
                                        onChange={(e) => updateField("description", e.target.value)}
                                    ></textarea>
                                    {getError("description")}
                                </div>

                                <div className="div-block-133">
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Cena / napomena</label>
                                        <input className="form-input grey w-input" placeholder="npr. Slobodan ulaz" type="text" name="localEventPriceText" maxLength={100}
                                            value={localEvent.priceText}
                                            onChange={(e) => updateField("priceText", e.target.value)}
                                        />
                                        {getError("priceText")}
                                    </div>
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Link za rezervaciju</label>
                                        <input className="form-input grey w-input" placeholder="npr. https://example.com/rezervacija" type="text" name="localEventReservationUrl"
                                            value={localEvent.reservationUrl}
                                            onChange={(e) => updateField("reservationUrl", e.target.value)}
                                        />
                                        {getError("reservationUrl")}
                                    </div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Upload slike</label>
                                    <input className="w-input" placeholder="npr. Margarita, Carbonara" type="file"
                                        onChange={uploadImage}
                                    />
                                    <div className="modal-image-div">
                                        <img alt="" className="modal-image"
                                            src={
                                                previewUrl ||
                                                (imageUrl ? imageUrl + "@lg.webp" : "")
                                            }
                                        />
                                        <div className="modal-image-remove-div" style={{ cursor: "pointer" }} >
                                            <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-16"
                                                onClick={() => {
                                                    setLocalEvent((previousEvent) => ({
                                                        ...previousEvent,
                                                        imageFile: undefined,
                                                        removeImage: true
                                                    }));

                                                    setImageUrl("");
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {getError("imageFile")}
                                </div>

                                <div className="div-block-133">
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Početak događaja *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(localEvent.startsAtUtc as Date)}
                                            onChange={(e) => updateField("startsAtUtc", new Date(e.target.value))}
                                        />
                                        {getError("startsAtUtc")}
                                    </div>
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Kraj događaja *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(localEvent.endsAtUtc as Date)}
                                            onChange={(e) => updateField("endsAtUtc", new Date(e.target.value))}
                                        />
                                        {getError("endsAtUtc")}
                                    </div>
                                </div>

                                <div className="div-block-133">
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Vidljiv od *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(localEvent.visibleFromUtc as Date)}
                                            onChange={(e) => updateField("visibleFromUtc", new Date(e.target.value))}
                                        />
                                        {getError("visibleFromUtc")}
                                    </div>
                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Vidljiv do *</label>
                                        <input className="form-input grey w-input" placeholder="npr. Margarita, Carbonara" type="datetime-local"
                                            value={formatDateForInput(localEvent.visibleUntilUtc as Date)}
                                            onChange={(e) => updateField("visibleUntilUtc", new Date(e.target.value))}
                                        />
                                        {getError("visibleUntilUtc")}
                                    </div>
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Objavljen</label>
                                    <Switch
                                        checked={localEvent.isPublished}
                                        onChange={(e) => updateField("isPublished", e.target.checked)}
                                    />
                                    {getError("isPublished")}
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Istaknut</label>
                                    <Switch
                                        checked={localEvent.isFeatured}
                                        onChange={(e) => updateField("isFeatured", e.target.checked)}
                                    />
                                    {getError("isFeatured")}
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Otkazan</label>
                                    <Switch
                                        checked={localEvent.isCancelled}
                                        onChange={(e) => updateField("isCancelled", e.target.checked)}
                                    />
                                    {getError("isCancelled")}
                                </div>

                            </form>
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium" onClick={onClose}>
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium" onClick={handleUpdateLocalEvent} >
                                <div>Ažuriraj</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}